import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Avatar,
  Upload,
  message,
  Divider,
  Space,
  Select,
  Tag,
  Alert,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  UploadOutlined,
  SaveOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useLocationData from "../../../hooks/useLocationData";
import { _upload_any_file } from "../../../common/axios_services";
import AutoCompleteAddress from "../../../components/AutoCompleteAddress";

const { TextArea } = Input;
const { Option } = Select;

const DeliveryProfile = ({ bikerProfile, onUpdateProfile, loading }) => {
  const [form] = Form.useForm();
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const {
    allStates,
    selectedState,
    setSelectedState,
    allLgas,
    loadingStates,
    loadingLgas,
  } = useLocationData();

  const VEHICLE_TYPE_OPTIONS = [
    {
      value: "BIKE",
      label: "Motorcycle/Bike",
      phase2Category: "bike",
      icon: "🏍️",
      description: "Fast single delivery",
      canDoNextDay: false,
      deliveryCapacity: "1 location",
    },
    {
      value: "CAR",
      label: "Car",
      phase2Category: "car",
      icon: "🚗",
      description: "Multi-package delivery",
      canDoNextDay: true,
      deliveryCapacity: "2-4 locations",
    },
    {
      value: "VAN",
      label: "Van",
      phase2Category: "van",
      icon: "🚐",
      description: "Bulk & cluster delivery",
      canDoNextDay: true,
      deliveryCapacity: "5+ locations",
    },
  ];

  const getVehicleConfig = (vehicleValue) => {
    return VEHICLE_TYPE_OPTIONS.find((opt) => opt.value === vehicleValue);
  };

  useEffect(() => {
    if (bikerProfile) {
      const bikers = bikerProfile?.bikers || {};
      const accountPersonal = bikerProfile?.account_personal || {};

      const vehicleMode = bikers.deliveryMode || "";
      setSelectedVehicleType(vehicleMode);

      form.setFieldsValue({
        firstName: accountPersonal.firstName || "",
        lastName: accountPersonal.lastName || "",
        email: accountPersonal.email || "",
        phone: accountPersonal.phoneNumber || "",
        address: bikers.detailAddress || bikers.homeAddress || "",
        vehicleType: vehicleMode,
        vehicleNumber: bikers.vehicleNumber || "",
        licenseNumber: bikers.licenseNumber || "",
        emergencyContact: bikers.emergencyContact || "",
        bankName: bikers.bankName || "",
        accountNumber: bikers.accountNumber || "",
        experience: bikers.experience || "",
        nin: bikers.nin || "",
        preferredArea: bikers.preferredArea || "",
        state: bikers.state || "",
        lga: bikers.lga || "",
      });
    }
  }, [bikerProfile, form]);

  useEffect(() => {
    if (form.getFieldValue("state")) {
      setSelectedState(form.getFieldValue("state"));
      form.setFieldsValue({ lga: undefined });
    }
  }, [form, setSelectedState]);

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phone,
        detailAddress: values.address,
        deliveryMode: values.vehicleType,
        vehicleNumber: values.vehicleNumber,
        licenseNumber: values.licenseNumber,
        emergencyContact: values.emergencyContact,
        bankName: values.bankName,
        accountNumber: values.accountNumber,
        experience: values.experience,
        nin: values.nin,
        preferredArea: values.preferredArea,
        state: values.state,
        lga: values.lga,
      };

      await onUpdateProfile(submitData);

      form.setFieldsValue(values);

      message.success("Profile updated successfully!");
    } catch (error) {
      message.error("Failed to update profile. Please try again.");
    }
  };

  const handleFileSelect = async (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      message.error("Image must be smaller than 10MB!");
      return false;
    }

    setUploadLoading(true);

    try {
      const uploadResult = await _upload_any_file(file);
      const imageUrl =
        uploadResult.data?.data?.url || uploadResult.data?.fileUrl;
      await onUpdateProfile({
        recentPictureUrl: imageUrl,
      });
      setUploadedImageUrl(imageUrl);
      message.success("Image uploaded successfully!");
    } catch (error) {
      message.error("Failed to upload image. Please try again.");
    } finally {
      setUploadLoading(false);
    }

    return false;
  };

  const uploadProps = {
    beforeUpload: handleFileSelect,
    showUploadList: false,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card>
        <div className="flex items-center space-x-4">
          <Avatar
            size={80}
            icon={<UserOutlined />}
            className="flex-shrink-0"
            src={uploadedImageUrl || bikerProfile?.bikers?.recentPictureUrl}
          />
          <div className="flex-1">
            <h2 className="text-2xl font-bold">
              {bikerProfile?.account_personal?.firstName}{" "}
              {bikerProfile?.account_personal?.lastName}
            </h2>
            <p className="text-gray-600">
              {bikerProfile?.account_personal?.email}
            </p>
            <p className="text-sm text-gray-500">
              Biker ID: {bikerProfile?.account_personal?.accountId} | Status:{" "}
              {bikerProfile?.bikers?.isAvailable ? "Available" : "Unavailable"}
            </p>
          </div>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={uploadLoading}>
              Change Photo
            </Button>
          </Upload>
        </div>
      </Card>

      {/* Profile Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-6"
      >
        {/* Personal Information */}
        <Card title="Personal Information" bordered={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                { required: true, message: "Please enter your first name" },
              ]}
            >
              <Input placeholder="Enter first name" disabled />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                { required: true, message: "Please enter your last name" },
              ]}
            >
              <Input placeholder="Enter last name" disabled />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" prefix={<MailOutlined />} disabled />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter your phone number" },
              ]}
            >
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Enter phone number"
                prefix={<PhoneOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Address"
              name="address"
              className="md:col-span-2"
              rules={[{ required: true, message: "Please enter your address" }]}
            >
              <AutoCompleteAddress
                placeholder="Enter your address"
              />
            </Form.Item>

            <Form.Item
              label="Emergency Contact"
              name="emergencyContact"
              rules={[
                { required: true, message: "Please enter emergency contact" },
              ]}
            >
              <Input
                placeholder="Emergency contact phone number"
                type="number"
                inputMode="numeric"
                prefix={<PhoneOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Years of Experience"
              name="experience"
              rules={[
                { required: true, message: "Please enter years of experience" },
              ]}
            >
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Enter years of delivery experience"
              />
            </Form.Item>

            <Form.Item
              label="NIN"
              name="nin"
              rules={[
                {
                  required: true,
                  message: "Please enter NIN",
                  pattern: /^\d{11}$/,
                },
              ]}
            >
              <Input
                placeholder="Enter National Identification Number"
                type="number"
                inputMode="numeric"
                maxLength={11}
              />
            </Form.Item>

            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: "Please select state" }]}
            >
              <Select
                placeholder="Select state"
                loading={loadingStates}
                onChange={(value) => {
                  setSelectedState(value);
                  form.setFieldsValue({
                    lga: undefined,
                    preferredArea: undefined,
                  });
                }}
              >
                {allStates.map((state) => (
                  <Option key={state?.id} value={state?.name}>
                    {state?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Local Area"
              name="lga"
              rules={[{ required: true, message: "Please select LGA" }]}
            >
              <Select placeholder="Select Local Area" loading={loadingLgas}>
                {allLgas.map((lga) => (
                  <Option key={lga?.id} value={lga?.name}>
                    {lga?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Preferred Delivery Area"
              name="preferredArea"
              rules={[
                {
                  required: true,
                  message: "Please select preferred delivery area",
                },
              ]}
            >
              <Select
                placeholder="Select preferred delivery area"
                loading={loadingLgas}
              >
                {allLgas.map((lga) => (
                  <Option key={lga?.id} value={lga?.name}>
                    {lga?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Card>

        {/* Vehicle Information */}
        <Card
          title="Vehicle Information"
          bordered={false}
          extra={
            selectedVehicleType && (
              <Tooltip
                title={`Phase 2: Can do next-day delivery: ${
                  getVehicleConfig(selectedVehicleType)?.canDoNextDay
                    ? "Yes"
                    : "No"
                }`}
              >
                <Tag
                  color={
                    getVehicleConfig(selectedVehicleType)?.canDoNextDay
                      ? "green"
                      : "orange"
                  }
                >
                  <InfoCircleOutlined /> Next-Day:{" "}
                  {getVehicleConfig(selectedVehicleType)?.canDoNextDay
                    ? "✓ Eligible"
                    : "✗ Not Eligible"}
                </Tag>
              </Tooltip>
            )
          }
        >
          <Alert
            message="Vehicle Type for Phase 2 Next-Day Delivery"
            description="Only Car and Van vehicles can participate in next-day cluster deliveries. Bikes, Motorcycles, and Bicycles are limited to same-day deliveries."
            type="info"
            showIcon
            style={{ marginBottom: "20px" }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <Form.Item
              label={
                <span>
                  Delivery Mode{" "}
                  <Tooltip title="Select the type of vehicle you use for deliveries. This affects which delivery types you can accept.">
                    <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                  </Tooltip>
                </span>
              }
              name="vehicleType"
              className="md:col-span-2"
              rules={[
                { required: true, message: "Please select delivery mode" },
              ]}
            >
              <Select
                placeholder="Select your vehicle type"
                onChange={(value) => setSelectedVehicleType(value)}
              >
                {VEHICLE_TYPE_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>{option.icon}</span>
                      <span>{option.label}</span>
                      {option.canDoNextDay && (
                        <Tag color="green" style={{ marginLeft: "auto" }}>
                          Next-Day ✓
                        </Tag>
                      )}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedVehicleType && (
              <div className="md:col-span-2" style={{ marginTop: "-12px" }}>
                <Card
                  size="small"
                  style={{
                    backgroundColor: getVehicleConfig(selectedVehicleType)
                      ?.canDoNextDay
                      ? "#f6ffed"
                      : "#fffbe6",
                    borderColor: getVehicleConfig(selectedVehicleType)
                      ?.canDoNextDay
                      ? "#b7eb8f"
                      : "#ffe58f",
                  }}
                >
                  <div style={{ fontSize: "14px" }}>
                    <p>
                      <strong>
                        {getVehicleConfig(selectedVehicleType)?.icon}{" "}
                        {getVehicleConfig(selectedVehicleType)?.label}
                      </strong>
                    </p>
                    <p style={{ margin: "4px 0", color: "#666" }}>
                      {getVehicleConfig(selectedVehicleType)?.description}
                    </p>
                    <p style={{ margin: "4px 0", color: "#666" }}>
                      <strong>Capacity:</strong>{" "}
                      {getVehicleConfig(selectedVehicleType)?.deliveryCapacity}
                    </p>
                    <p style={{ margin: "8px 0" }}>
                      <strong>Next-Day Delivery Eligible:</strong>{" "}
                      <Tag
                        color={
                          getVehicleConfig(selectedVehicleType)?.canDoNextDay
                            ? "green"
                            : "orange"
                        }
                      >
                        {getVehicleConfig(selectedVehicleType)?.canDoNextDay
                          ? "Yes ✓"
                          : "No ✗"}
                      </Tag>
                    </p>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </Card>

        <Card title="Additional Vehicle Details (Optional)" bordered={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <Form.Item
              label="Vehicle Number/Plate"
              name="vehicleNumber"
            >
              <Input placeholder="Enter vehicle registration number" />
            </Form.Item>

            <Form.Item
              label="License Number"
              name="licenseNumber"
            >
              <Input placeholder="Enter driving license number" />
            </Form.Item>
          </div>
        </Card>

        <Card
          title="Phase 2: Next-Day Delivery Program"
          bordered={false}
          style={{ backgroundColor: "#f0f5ff", borderColor: "#91d5ff" }}
        >
          <div style={{ padding: "16px" }}>
            <p style={{ marginBottom: "12px" }}>
              <strong>📦 What is Next-Day Delivery?</strong>
            </p>
            <p style={{ marginBottom: "12px", color: "#666" }}>
              Next-day delivery allows you to participate in cluster deliveries
              where multiple orders are grouped together for efficient route
              optimization. Orders are grouped at 9PM the previous day and you
              can accept clusters starting from 12 AM.
            </p>

            <p style={{ marginBottom: "12px" }}>
              <strong>✅ Requirements:</strong>
            </p>
            <ul style={{ color: "#666", marginBottom: "12px" }}>
              <li>
                Vehicle Type: Car or Van (Your current:{" "}
                <strong>
                  {getVehicleConfig(selectedVehicleType)?.label ||
                    "Not selected"}
                </strong>
                )
              </li>
              <li>Confirm ready by 9AM on delivery date</li>
              <li>Participate in cluster-based allocation</li>
              <li>Complete all deliveries in assigned cluster</li>
            </ul>

            <p style={{ marginBottom: "12px" }}>
              <strong>💰 Earnings:</strong>
            </p>
            <p style={{ color: "#666" }}>
              Earn per-cluster bonuses + per-delivery fees. Rates depend on
              cluster size and delivery distance.
            </p>

            {selectedVehicleType &&
              !getVehicleConfig(selectedVehicleType)?.canDoNextDay && (
                <Alert
                  message="Your vehicle type doesn't qualify for next-day delivery yet"
                  description="Upgrade to a Car or Van to start accepting next-day cluster deliveries."
                  type="warning"
                  showIcon
                  style={{ marginTop: "12px" }}
                />
              )}

            {selectedVehicleType &&
              getVehicleConfig(selectedVehicleType)?.canDoNextDay && (
                <Alert
                  message="Great! Your vehicle qualifies for next-day delivery"
                  description="You'll be able to see and accept next-day delivery clusters once Phase 2 launches."
                  type="success"
                  showIcon
                  style={{ marginTop: "12px" }}
                />
              )}
          </div>
        </Card>

        {/* Submit Button */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >
              Update Profile
            </Button>
            <Button onClick={() => form.resetFields()} size="large">
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default DeliveryProfile;
